/**
 * Constructive Feedback Component for Inside Sales Training
 * Shows detailed feedback after each section with strengths and areas to improve
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
    CheckCircle2, AlertCircle, Lightbulb, TrendingUp, 
    Target, Award, ChevronRight, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * FeedbackBento - A comprehensive feedback display component
 * @param {Object} props
 * @param {string} props.type - 'quiz' | 'simulation' | 'teaching'
 * @param {number} props.score - Score from 0-1
 * @param {string[]} props.strengths - What the user did well
 * @param {string[]} props.improvements - Areas to improve
 * @param {string} props.overallFeedback - Summary feedback text
 * @param {string[]} props.actionableAdvice - Specific tips for improvement
 * @param {Object} props.metrics - Additional metrics (e.g., clarity, confidence, knowledge)
 * @param {Function} props.onContinue - Callback when user clicks continue
 * @param {string} props.continueLabel - Label for continue button
 */
const ConstructiveFeedback = ({ 
    type = 'quiz',
    score = 0,
    strengths = [],
    improvements = [],
    overallFeedback = '',
    actionableAdvice = [],
    metrics = {},
    onContinue,
    continueLabel = 'Continue'
}) => {
    const scorePercent = Math.round(score * 100);
    
    const getScoreColor = () => {
        if (scorePercent >= 80) return 'text-emerald-400';
        if (scorePercent >= 60) return 'text-amber-400';
        return 'text-rose-400';
    };

    const getScoreBg = () => {
        if (scorePercent >= 80) return 'from-emerald-500/20 to-teal-500/20 border-emerald-500/30';
        if (scorePercent >= 60) return 'from-amber-500/20 to-orange-500/20 border-amber-500/30';
        return 'from-rose-500/20 to-red-500/20 border-rose-500/30';
    };

    const getScoreLabel = () => {
        if (scorePercent >= 90) return 'Excellent!';
        if (scorePercent >= 80) return 'Great Job!';
        if (scorePercent >= 70) return 'Good Progress';
        if (scorePercent >= 60) return 'Keep Practicing';
        return 'Needs Work';
    };

    const typeLabels = {
        quiz: 'Quiz Performance',
        simulation: 'Simulation Results',
        teaching: 'Section Complete'
    };

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="constructive-feedback">
            {/* Score Header */}
            <div className={`p-8 rounded-3xl bg-gradient-to-br ${getScoreBg()} border text-center`}>
                <Badge variant="secondary" className="mb-4 bg-white/10 text-white/70">
                    {typeLabels[type]}
                </Badge>
                
                <div className={`text-6xl font-bold ${getScoreColor()} mb-2`}>
                    {scorePercent}%
                </div>
                <p className="text-xl text-white/80">{getScoreLabel()}</p>
                
                {/* Metric Bars */}
                {Object.keys(metrics).length > 0 && (
                    <div className="mt-6 space-y-3 max-w-xs mx-auto">
                        {Object.entries(metrics).map(([key, value]) => (
                            <div key={key}>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-white/60 capitalize">{key.replace(/_/g, ' ')}</span>
                                    <span className="text-white/80">{Math.round(value * 100)}%</span>
                                </div>
                                <Progress value={value * 100} className="h-2 bg-white/10" />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Overall Feedback */}
            {overallFeedback && (
                <Card className="bg-[hsl(240,6%,10%)] border-white/10">
                    <CardContent className="p-6">
                        <p className="text-white/90 leading-relaxed">{overallFeedback}</p>
                    </CardContent>
                </Card>
            )}

            {/* Bento Grid: Strengths & Improvements */}
            <div className="grid md:grid-cols-2 gap-4">
                {/* What You Did Well */}
                <Card className="bg-emerald-500/5 border-emerald-500/20">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-emerald-400 text-lg">
                            <CheckCircle2 className="w-5 h-5" />
                            What You Did Well
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {strengths.length > 0 ? (
                            <ul className="space-y-3">
                                {strengths.map((item, index) => (
                                    <li 
                                        key={`strength-${index}`}
                                        className="flex items-start gap-3 text-white/80"
                                    >
                                        <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                        </div>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-white/50 text-sm">No specific strengths identified yet. Keep practicing!</p>
                        )}
                    </CardContent>
                </Card>

                {/* Areas to Improve */}
                <Card className="bg-amber-500/5 border-amber-500/20">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-amber-400 text-lg">
                            <TrendingUp className="w-5 h-5" />
                            Areas to Improve
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {improvements.length > 0 ? (
                            <ul className="space-y-3">
                                {improvements.map((item, index) => (
                                    <li 
                                        key={`improvement-${index}`}
                                        className="flex items-start gap-3 text-white/80"
                                    >
                                        <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                                        </div>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-white/50 text-sm">Great job! No major areas identified for improvement.</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Actionable Advice */}
            {actionableAdvice.length > 0 && (
                <Card className="bg-indigo-500/5 border-indigo-500/20">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-indigo-400 text-lg">
                            <Lightbulb className="w-5 h-5" />
                            Tips for Next Time
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <ul className="space-y-3">
                            {actionableAdvice.map((tip, index) => (
                                <li 
                                    key={`tip-${index}`}
                                    className="flex items-start gap-3 text-white/80"
                                >
                                    <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
                                    </div>
                                    <span>{tip}</span>
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            )}

            {/* Continue Button */}
            {onContinue && (
                <div className="flex justify-center pt-4">
                    <Button 
                        onClick={onContinue}
                        size="lg"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-12 py-6 text-lg"
                        data-testid="feedback-continue-btn"
                    >
                        {continueLabel}
                        <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            )}
        </div>
    );
};

/**
 * QuickFeedback - A smaller inline feedback component for teaching sections
 */
export const QuickFeedback = ({ keyTakeaways = [], tips = [] }) => {
    if (keyTakeaways.length === 0 && tips.length === 0) return null;

    return (
        <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10 space-y-4" data-testid="quick-feedback">
            {keyTakeaways.length > 0 && (
                <div>
                    <h4 className="text-sm font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Key Takeaways
                    </h4>
                    <ul className="space-y-1">
                        {keyTakeaways.map((item, index) => (
                            <li key={`takeaway-${index}`} className="text-sm text-white/70 flex items-start gap-2">
                                <span className="text-emerald-400 mt-1">•</span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            
            {tips.length > 0 && (
                <div>
                    <h4 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" />
                        Remember
                    </h4>
                    <ul className="space-y-1">
                        {tips.map((item, index) => (
                            <li key={`tip-${index}`} className="text-sm text-white/70 flex items-start gap-2">
                                <span className="text-amber-400 mt-1">•</span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ConstructiveFeedback;
